import {
    createAction,
    createAsyncThunk,
    createSlice,
    current,
    PayloadAction
} from '@reduxjs/toolkit'
import { sendInteractionsAction } from 'actions/interactionsActions'
import {
    clientDetailsEnrollmentAction,
    updatePackagesAction
} from 'features/enrollment/enrollmentSlice'
import {
    constants,
    formatPayloadToClientObject,
    generateUuidv4,
    getConnectUser,
    sendMessageToWorkerWithResponse
} from '../../helpers'
import {
    BulkEnrollmentSelectedAddOn,
    BulkEnrollmentSelectedPackage,
    buildBulkEnrollmentRowPackets,
    composeBulkEnrollmentFullname
} from '../../helpers/bulkEnrollmentHelpers'
import { RootState } from '../../store/configStore'
import { AuthSliceNamesEnum, ClientType } from '../../storeTypes'
import { ClientStatus } from '../../storeTypes/clientTypes'
import { InteractionOutcomes } from '../../types/interactionOutcomes'
import { InteractionSources } from '../../types/interactionSources'

export type BulkEnrollmentRow = {
    id: string
    phone: string
    quantity: string
    matchedClient: ClientType | null
    confirmed: boolean
    committed: boolean
    enrolled: boolean
    clientCode: string
    firstname: string
    lastname: string
    selectedAddOns: BulkEnrollmentSelectedAddOn[]
}

type LoadBulkEnrollmentClientsPayload = {
    villageId?: string
    veVillages?: { sf_id: string }[]
}

type BulkEnrollmentState = {
    /** Village the lot in progress belongs to; null until a lot is started. */
    villageId: string | null
    selectedPackage: BulkEnrollmentSelectedPackage | null
    rows: BulkEnrollmentRow[]
    clients: ClientType[]
    activeDropdownRowId: string | null
    loading: boolean
    submitting: boolean
    error: string
    submitError: string
}

const createEmptyRow = (id: string): BulkEnrollmentRow => ({
    id,
    phone: '',
    quantity: '1',
    matchedClient: null,
    confirmed: false,
    committed: false,
    enrolled: false,
    clientCode: '',
    firstname: '',
    lastname: '',
    selectedAddOns: []
})

const createInitialRows = (): BulkEnrollmentRow[] => [createEmptyRow('1')]

const buildInitialState = (): BulkEnrollmentState => ({
    villageId: null,
    selectedPackage: null,
    rows: createInitialRows(),
    clients: [],
    activeDropdownRowId: null,
    loading: false,
    submitting: false,
    error: '',
    submitError: ''
})

/**
 * The lot in progress (selected package + rows) is persisted to localStorage so
 * a page refresh or browser tab crash mid-entry does not lose the agent's work.
 * It is cleared when the lot is reset/initialised or the user logs out.
 */
export const BULK_ENROLLMENT_LOT_STORAGE_KEY = 'bulk_enrollment_lot'

type PersistedBulkEnrollmentLot = Pick<
    BulkEnrollmentState,
    'villageId' | 'selectedPackage' | 'rows'
>

const isEmptyRow = (row: BulkEnrollmentRow) =>
    !row.phone &&
    !row.firstname &&
    !row.lastname &&
    !row.matchedClient &&
    row.quantity === '1'

const isPristineLot = (lot: PersistedBulkEnrollmentLot) =>
    !lot.selectedPackage && lot.rows.length === 1 && isEmptyRow(lot.rows[0])

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value)

const hasStringFields = (value: Record<string, unknown>, fields: string[]) =>
    fields.every((field) => typeof value[field] === 'string')

const isPersistedMatchedClient = (value: unknown): value is ClientType =>
    isRecord(value) &&
    typeof value.client_code === 'string' &&
    value.client_code.trim().length > 0

const isPersistedRow = (value: unknown): value is BulkEnrollmentRow =>
    isRecord(value) &&
    hasStringFields(value, [
        'id',
        'phone',
        'quantity',
        'firstname',
        'lastname'
    ]) &&
    typeof value.confirmed === 'boolean' &&
    (value.matchedClient === null ||
        isPersistedMatchedClient(value.matchedClient))

const isPersistedPackage = (
    value: unknown
): value is BulkEnrollmentSelectedPackage | null =>
    value === null ||
    (isRecord(value) &&
        hasStringFields(value, [
            'product_id',
            'pricebook_id',
            'package_id',
            'name',
            'price',
            'size'
        ]))

export const readPersistedBulkEnrollmentLot =
    (): PersistedBulkEnrollmentLot | null => {
        try {
            const raw = localStorage.getItem(BULK_ENROLLMENT_LOT_STORAGE_KEY)
            if (!raw) return null
            const parsed: unknown = JSON.parse(raw)
            if (!isRecord(parsed)) return null

            const { villageId, rows } = parsed
            const selectedPackage = parsed.selectedPackage ?? null
            if (
                typeof villageId !== 'string' ||
                !Array.isArray(rows) ||
                rows.length === 0 ||
                !rows.every(isPersistedRow) ||
                !isPersistedPackage(selectedPackage)
            ) {
                return null
            }

            return { villageId, selectedPackage, rows }
        } catch {
            return null
        }
    }

export const clearPersistedBulkEnrollmentLot = () => {
    try {
        localStorage.removeItem(BULK_ENROLLMENT_LOT_STORAGE_KEY)
    } catch {
        // storage unavailable (private mode, quota); nothing to clear
    }
}

const persistLot = (lot: PersistedBulkEnrollmentLot) => {
    try {
        if (isPristineLot(lot) || !lot.villageId) {
            localStorage.removeItem(BULK_ENROLLMENT_LOT_STORAGE_KEY)
            return
        }
        localStorage.setItem(
            BULK_ENROLLMENT_LOT_STORAGE_KEY,
            JSON.stringify({
                villageId: lot.villageId,
                selectedPackage: lot.selectedPackage,
                rows: lot.rows
            })
        )
    } catch {
        // storage unavailable (private mode, quota); the in-memory lot still works
    }
}

const persistDraft = (state: BulkEnrollmentState) => {
    const { villageId, selectedPackage, rows } = current(state)
    persistLot({ villageId, selectedPackage, rows })
}

const buildFreshStateForVillage = (
    villageId?: string | null
): BulkEnrollmentState => ({
    ...buildFreshState(),
    villageId: villageId ?? null
})

const buildFreshState = (): BulkEnrollmentState => {
    clearPersistedBulkEnrollmentLot()
    return buildInitialState()
}

const initialState: BulkEnrollmentState = {
    ...buildInitialState(),
    ...(readPersistedBulkEnrollmentLot() || {})
}

export const loadBulkEnrollmentClients = createAsyncThunk(
    'bulkEnrollment/loadBulkEnrollmentClients',
    async (
        { villageId, veVillages = [] }: LoadBulkEnrollmentClientsPayload,
        { rejectWithValue }
    ) => {
        try {
            const villageIds = villageId
                ? [villageId]
                : veVillages.map(({ sf_id: sfId }) => sfId)

            if (!villageIds.length) {
                return []
            }

            const response = (await sendMessageToWorkerWithResponse({
                [constants.MESSAGES.GET_CLIENTS]: {
                    clientType: constants.CLIENT_TYPES.ALL_CLIENT,
                    villageIds,
                    filter: 'village'
                }
            })) as { storedData?: ClientType[] }

            return (response?.storedData || []).filter(
                (client) => !client._is_deleted
            )
        } catch {
            return rejectWithValue('Failed to load village clients')
        }
    }
)

type DispatchedThunk = Promise<unknown> & {
    unwrap: () => Promise<unknown>
}

type DispatchWithUnwrap = (action: unknown) => DispatchedThunk

export const markBulkEnrollmentRowEnrolled = createAction<{
    rowId: string
    clientCode: string
}>('bulkEnrollment/markBulkEnrollmentRowEnrolled')

const applyRowEnrolled = (
    state: BulkEnrollmentState,
    rowId: string,
    clientCode: string
) => {
    const row = state.rows.find((item) => item.id === rowId)
    if (!row) return

    row.enrolled = true
    row.clientCode = clientCode
    persistDraft(state)
}

type SettledEnrollmentAction = {
    type?: string
    payload?: unknown
    meta?: { requestStatus?: string }
}

const isHttpEnrollmentFailure = (action: SettledEnrollmentAction) => {
    const payload = action.payload
    return (
        action.meta?.requestStatus === 'rejected' &&
        typeof payload === 'object' &&
        payload !== null &&
        'httpStatus' in payload
    )
}

const settleEnrollmentDispatch = async (
    dispatched: DispatchedThunk,
    requireSuccess: boolean
) => {
    if (requireSuccess) {
        return dispatched.unwrap()
    }
    const action = (await dispatched) as SettledEnrollmentAction
    if (isHttpEnrollmentFailure(action)) {
        throw action.payload
    }
    return action.payload
}

const isSubmittableBulkEnrollmentRow = (row: BulkEnrollmentRow) => {
    const quantity = Number(row.quantity)
    return Boolean(
        row.committed && !row.enrolled && row.phone && quantity && quantity >= 1
    )
}

const allocateBlankClientCode = async () => {
    const blankIdResponse = await sendMessageToWorkerWithResponse({
        'get blank id': 'client needs a blank id'
    })
    const clientCode = blankIdResponse.blank_id.code.toString()
    // Consume-on-get in the worker is the source of uniqueness. Await delete
    // so older workers that still peek cannot reuse the same code.
    await sendMessageToWorkerWithResponse({
        'delete blank id': clientCode
    })
    return clientCode
}

const resolveBulkEnrollmentClientCode = async (row: BulkEnrollmentRow) => {
    const existing = String(
        row.clientCode || row.matchedClient?.client_code || ''
    ).trim()
    if (existing) {
        return existing
    }
    return allocateBlankClientCode()
}

const enrollBulkEnrollmentClient = async (
    row: BulkEnrollmentRow,
    selectedPackage: BulkEnrollmentSelectedPackage,
    villageId: string,
    dispatch: DispatchWithUnwrap,
    { requireSuccess = false }: { requireSuccess?: boolean } = {}
) => {
    const { username } = getConnectUser()
    const quantity = Number(row.quantity)
    const packets = buildBulkEnrollmentRowPackets(
        selectedPackage,
        quantity,
        row.selectedAddOns,
        row.matchedClient
    )

    const matchedClient = row.matchedClient
    const clientCode = await resolveBulkEnrollmentClientCode(row)
    const firstname = row.firstname || ''
    const lastname = row.lastname || ''
    const fullname = composeBulkEnrollmentFullname(firstname, lastname)

    const clientDetailsPayload = matchedClient
        ? {
              phone: row.phone || matchedClient.phone,
              firstname,
              lastname,
              fullname,
              client_code: clientCode,
              farmerIdBlankId: clientCode,
              username,
              date: new Date().toISOString(),
              form_type:
                  matchedClient.form_type || constants.FORM_TYPES.ENROLLMENT,
              village: villageId,
              village_id: villageId,
              client_status:
                  matchedClient.client_status || ClientStatus.POTENTIAL,
              uuid: matchedClient.uuid || generateUuidv4(),
              sex: matchedClient.sex || undefined,
              group_leader: matchedClient.group_leader,
              head_of_household: matchedClient.head_of_household
          }
        : {
              phone: row.phone,
              firstname,
              lastname,
              fullname,
              client_code: clientCode,
              farmerIdBlankId: clientCode,
              username,
              date: new Date().toISOString(),
              form_type: constants.FORM_TYPES.ENROLLMENT,
              village: villageId,
              village_id: villageId,
              client_status: ClientStatus.POTENTIAL,
              uuid: generateUuidv4()
          }

    const ordersPayload = {
        phone: row.phone,
        firstname,
        lastname,
        client_code: clientCode,
        packets,
        goal_items: matchedClient?.goal_items || [],
        form_type: matchedClient?.form_type || constants.FORM_TYPES.ENROLLMENT,
        village: villageId,
        village_id: villageId,
        client_status: ClientStatus.PACKAGE_WITHOUT_PAYMENT,
        balance: matchedClient?.balance || 0,
        fullname
    }

    const formattedOrders = formatPayloadToClientObject({
        ...ordersPayload
    })

    formattedOrders.goal_items = [
        ...(matchedClient?.goal_items || []).filter((item) => !item.dropped),
        ...(formattedOrders.goal_items || [])
    ]

    const postClientAndOrders = async () => {
        await settleEnrollmentDispatch(
            dispatch(
                clientDetailsEnrollmentAction({
                    clientDetailsPayload,
                    gpsCoords: {
                        latitude: null,
                        longitude: null,
                        accuracy: null
                    }
                })
            ),
            requireSuccess
        )

        dispatch(
            sendInteractionsAction({
                survey_data: { orders: packets },
                client_code: clientCode,
                client_status:
                    matchedClient?.client_status || ClientStatus.POTENTIAL,
                user_id: username,
                survey_name: constants.BULK_ENROLLMENT_SURVEY_NAME,
                survey_version: constants.BULK_ENROLLMENT_SURVEY_VERSION,
                village_id: villageId,
                source: InteractionSources.CONNECT
            })
        )

        dispatch(
            sendInteractionsAction({
                client_code: clientCode,
                client_status:
                    matchedClient?.client_status || ClientStatus.POTENTIAL,
                user_id: username,
                survey_name: constants.BULK_ENROLLMENT_SURVEY_NAME,
                survey_version: constants.BULK_ENROLLMENT_SURVEY_VERSION,
                village_id: villageId,
                outcome: InteractionOutcomes.ENROLLED,
                source: InteractionSources.CONNECT
            })
        )

        await settleEnrollmentDispatch(
            dispatch(
                updatePackagesAction({
                    clientDetails: formattedOrders,
                    existingOrders: [],
                    newOrders: packets,
                    skipLocalPersist: true
                })
            ),
            requireSuccess
        )
    }

    // Await each POST. An HTTP rejection fails the row before anything is
    // written locally. A network rejection is queued by the service worker,
    // so the row is saved locally and marked enrolled.
    await postClientAndOrders()

    await sendMessageToWorkerWithResponse({
        'update client details with orders': formattedOrders
    })

    return { clientCode }
}

export const enrollBulkEnrollmentRow = createAsyncThunk(
    'bulkEnrollment/enrollBulkEnrollmentRow',
    async (
        { villageId, rowId }: { villageId: string; rowId: string },
        { getState, dispatch, rejectWithValue }
    ) => {
        const state = getState() as RootState
        const { rows, selectedPackage } = state.bulkEnrollment
        const row = rows.find((item) => item.id === rowId)

        if (!selectedPackage) {
            return rejectWithValue('bulkEnrollment.noPackageSelected')
        }

        if (!villageId) {
            return rejectWithValue('bulkEnrollment.missingVillage')
        }

        if (!row?.phone || Number(row.quantity) < 1) {
            return rejectWithValue('bulkEnrollment.incompleteClient')
        }

        if (row.enrolled && row.clientCode) {
            return { rowId, clientCode: row.clientCode }
        }

        try {
            const { clientCode } = await enrollBulkEnrollmentClient(
                row,
                selectedPackage,
                villageId,
                dispatch,
                { requireSuccess: true }
            )
            return { rowId, clientCode }
        } catch {
            return rejectWithValue('bulkEnrollment.enrollFailed')
        }
    }
)

const BULK_ENROLLMENT_POST_CONCURRENCY = 4

const settleInBatches = async <T>(
    items: T[],
    worker: (item: T) => Promise<void>
): Promise<PromiseSettledResult<void>[]> => {
    const results: PromiseSettledResult<void>[] = new Array(items.length)
    let nextIndex = 0
    const runNext = async () => {
        while (nextIndex < items.length) {
            const current = nextIndex
            nextIndex += 1
            try {
                await worker(items[current])
                results[current] = { status: 'fulfilled', value: undefined }
            } catch (reason) {
                results[current] = { status: 'rejected', reason }
            }
        }
    }
    const workers = Array.from(
        { length: Math.min(BULK_ENROLLMENT_POST_CONCURRENCY, items.length) },
        () => runNext()
    )
    await Promise.all(workers)
    return results
}

export const submitBulkEnrollment = createAsyncThunk(
    'bulkEnrollment/submitBulkEnrollment',
    async (
        { villageId }: { villageId: string },
        { getState, dispatch, rejectWithValue }
    ) => {
        const state = getState() as RootState
        const { rows, selectedPackage } = state.bulkEnrollment

        if (!selectedPackage) {
            return rejectWithValue('bulkEnrollment.noPackageSelected')
        }

        if (!villageId) {
            return rejectWithValue('bulkEnrollment.missingVillage')
        }

        try {
            const pendingRows = rows.filter(isSubmittableBulkEnrollmentRow)

            // Allocate inside each row. get blank id consumes, so overlapping
            // rows still get distinct codes. A later allocation failure does
            // not drop IDs already posted.
            const results = await settleInBatches(
                pendingRows,
                async (row) => {
                    const enrolled = await enrollBulkEnrollmentClient(
                        row,
                        selectedPackage,
                        villageId,
                        dispatch
                    )
                    dispatch(
                        markBulkEnrollmentRowEnrolled({
                            rowId: row.id,
                            clientCode: enrolled.clientCode
                        })
                    )
                }
            )

            if (results.some((result) => result.status === 'rejected')) {
                return rejectWithValue('bulkEnrollment.submitFailed')
            }

            return { rowCount: pendingRows.length }
        } catch {
            return rejectWithValue('bulkEnrollment.submitFailed')
        }
    }
)

const bulkEnrollmentSlice = createSlice({
    name: 'bulkEnrollment',
    initialState,
    reducers: {
        initializeBulkEnrollment: (
            _state,
            action: PayloadAction<string | undefined>
        ) => buildFreshStateForVillage(action.payload),
        /**
         * Make sure the lot in memory belongs to the village being viewed.
         * A draft rehydrated from storage (or left over from another village)
         * is discarded rather than submitted against the wrong village.
         */
        ensureBulkEnrollmentVillage: (state, action: PayloadAction<string>) => {
            if (state.villageId === action.payload) return state
            return buildFreshStateForVillage(action.payload)
        },
        selectBulkEnrollmentPackage: (
            state,
            action: PayloadAction<BulkEnrollmentSelectedPackage>
        ) => {
            state.selectedPackage = action.payload
            state.rows.forEach((row) => {
                row.selectedAddOns = []
            })
            persistDraft(state)
        },
        setBulkEnrollmentRows: (
            state,
            action: PayloadAction<BulkEnrollmentRow[]>
        ) => {
            state.rows = action.payload
            persistDraft(state)
        },
        addBulkEnrollmentRow: (state, action: PayloadAction<string>) => {
            state.rows.push(createEmptyRow(action.payload))
            persistDraft(state)
        },
        removeBulkEnrollmentRow: (state, action: PayloadAction<string>) => {
            state.rows = state.rows.filter((item) => item.id !== action.payload)

            if (state.activeDropdownRowId === action.payload) {
                state.activeDropdownRowId = null
            }

            if (state.rows.length === 0) {
                state.rows = createInitialRows()
            }
            persistDraft(state)
        },
        updateBulkEnrollmentRow: (
            state,
            action: PayloadAction<{
                id: string
                changes: Partial<Omit<BulkEnrollmentRow, 'id'>>
            }>
        ) => {
            const row = state.rows.find((item) => item.id === action.payload.id)

            if (row) {
                Object.assign(row, action.payload.changes)
                persistDraft(state)
            }
        },
        confirmBulkEnrollmentRow: (state, action: PayloadAction<string>) => {
            const row = state.rows.find((item) => item.id === action.payload)
            if (row?.matchedClient) {
                row.confirmed = true
                persistDraft(state)
            }
        },
        commitBulkEnrollmentRow: (state, action: PayloadAction<string>) => {
            const row = state.rows.find((item) => item.id === action.payload)
            if (row && !row.committed) {
                row.committed = true
                row.confirmed = true
                persistDraft(state)
            }
        },
        clearBulkEnrollmentRow: (state, action: PayloadAction<string>) => {
            const index = state.rows.findIndex(
                (item) => item.id === action.payload
            )
            const row = state.rows[index]
            if (index === -1 || !row || row.committed) {
                return
            }

            if (state.activeDropdownRowId === row.id) {
                state.activeDropdownRowId = null
            }

            state.rows[index] = createEmptyRow(row.id)
            persistDraft(state)
        },
        setBulkEnrollmentActiveDropdownRow: (
            state,
            action: PayloadAction<string | null>
        ) => {
            state.activeDropdownRowId = action.payload
        },
        clearBulkEnrollmentSubmitError: (state) => {
            state.submitError = ''
        },
        resetBulkEnrollment: () => buildFreshState()
    },
    extraReducers: (builder) => {
        builder
            .addCase(loadBulkEnrollmentClients.pending, (state) => {
                state.loading = true
                state.error = ''
            })
            .addCase(loadBulkEnrollmentClients.fulfilled, (state, action) => {
                state.loading = false
                state.clients = action.payload
                state.error = ''
            })
            .addCase(loadBulkEnrollmentClients.rejected, (state, action) => {
                state.loading = false
                state.clients = []
                state.error =
                    (action.payload as string) ||
                    'Failed to load village clients'
            })
            .addCase(markBulkEnrollmentRowEnrolled, (state, action) => {
                applyRowEnrolled(
                    state,
                    action.payload.rowId,
                    action.payload.clientCode
                )
            })
            .addCase(enrollBulkEnrollmentRow.fulfilled, (state, action) => {
                applyRowEnrolled(
                    state,
                    action.payload.rowId,
                    action.payload.clientCode
                )
            })
            .addCase(submitBulkEnrollment.pending, (state) => {
                state.submitting = true
                state.submitError = ''
            })
            .addCase(submitBulkEnrollment.fulfilled, (state) => {
                state.submitting = false
                state.submitError = ''
            })
            .addCase(submitBulkEnrollment.rejected, (state, action) => {
                state.submitting = false
                state.submitError =
                    (action.payload as string) || 'bulkEnrollment.submitFailed'
            })
            .addMatcher(
                (action) => action.type === AuthSliceNamesEnum.AUTH_LOGOUT,
                () => buildFreshState()
            )
    }
})

export const {
    initializeBulkEnrollment,
    ensureBulkEnrollmentVillage,
    selectBulkEnrollmentPackage,
    setBulkEnrollmentRows,
    addBulkEnrollmentRow,
    removeBulkEnrollmentRow,
    updateBulkEnrollmentRow,
    confirmBulkEnrollmentRow,
    commitBulkEnrollmentRow,
    clearBulkEnrollmentRow,
    setBulkEnrollmentActiveDropdownRow,
    clearBulkEnrollmentSubmitError,
    resetBulkEnrollment
} = bulkEnrollmentSlice.actions

export default bulkEnrollmentSlice.reducer
